import { browser } from '$app/environment';
import Cookies from 'js-cookie';
function shoudLogTracking(): boolean {
	if (!browser) return false;

	return Cookies.get('mme_tracking') === 'true';
}

export function trackForm(target: HTMLElement): void {
	if (shoudLogTracking()) {
		console.log('trackForm');
	}

	if (window?.wtmbm?.trackForm) {
		window.wtmbm.trackForm(target);
		if ('formAnonymous' in window.wtmbm) {
			window.wtmbm.formAnonymous = true;
		}
	}
}

export type ConstraintValidatableFormElements =
	| HTMLButtonElement
	| HTMLFieldSetElement
	| HTMLInputElement
	| HTMLObjectElement
	| HTMLOutputElement
	| HTMLSelectElement
	| HTMLTextAreaElement;

export const trackFormError = (formName: string, fieldName: string, error: string): void => {
	window?.utag?.link({
		wt_link_id: 'content.text.form_errormessage',
		action_form_errormessage: `${formName}.${fieldName}.${error}`
	});
};

export function getFormFieldErrors(validity: ValidityState): string[] {
	if (validity.valid) return [];

	const errorMap: Record<keyof ValidityState, string> = {
		badInput: 'bad-input',
		customError: 'custom-error',
		patternMismatch: 'pattern-mismatch',
		rangeOverflow: 'range-overflow',
		rangeUnderflow: 'range-underflow',
		stepMismatch: 'step-mismatch',
		tooLong: 'too-long',
		tooShort: 'too-short',
		typeMismatch: 'type-mismatch',
		valueMissing: 'value-missing',
		valid: 'valid'
	};

	const errors: string[] = [];

	Object.keys(errorMap).forEach((key) => {
		if (validity[key as keyof ValidityState]) {
			errors.push(errorMap[key as keyof ValidityState]);
		}
	});

	return errors;
}

export function trackFormFieldErrors(
	formName: string,
	validatableField: ConstraintValidatableFormElements
): void {
	const fieldName = validatableField.name;

	const errors = getFormFieldErrors(validatableField.validity);

	errors.forEach((err) => {
		trackFormError(formName, fieldName, err);
		if (shoudLogTracking()) {
			console.log(`${formName}.${fieldName}.${err}`);
		}
	});
}

export function sanitizeEventPageParams(str: string): string {
	return (
		str
			.trim()
			.toLowerCase()
			// replace german umlauts
			.replace(/\u00df/g, 'ss')
			.replace(/\u00e4/g, 'ae')
			.replace(/\u00f6/g, 'oe')
			.replace(/\u00fc/g, 'ue')
	);
}

function formatTealiumRelContent(sourceStr: string): string {
	return (
		sourceStr
			.trim()
			.toLowerCase()
			// replace german umlauts
			.replace(/\u00df/g, 'ss')
			.replace(/\u00e4/g, 'ae')
			.replace(/\u00f6/g, 'oe')
			.replace(/\u00fc/g, 'ue')
			// replace whitespace and underscores with hypens
			.replace(/\s+/g, '-')
			// replace multiple hyphens with single hyphen
			.replace(/-{2,}/g, '-')
			// remove any special characters expect hyphens
			.replace(/[^a-z0-9-]/g, '')
	);
}

export function parseMultipleValues(values: string[]): string {
	const sanitizedValues: string[] = [];

	values.forEach((value) => sanitizedValues.push(formatTealiumRelContent(value)));

	return sanitizedValues.join(';');
}

export function parseMultipleValuesRaw(values: string[]): string {
	return values.join(';');
}

export function trackEventFilters(
	locations: string[],
	topics: string[],
	eventTypes: string[],
	targetGroups: string[],
	eventName = ''
): void {
	const clickFilterLocation = parseMultipleValues(locations);
	const clickFilterTopic = parseMultipleValues(topics);
	const clickFilterEventType = parseMultipleValues(eventTypes);
	const clickFilterTargetGroup = parseMultipleValues(targetGroups);

	const filterTrackingParams = {
		wt_link_id: eventName || 'content.text.zum-event',
		click_filter_location: clickFilterLocation,
		click_filter_topic: clickFilterTopic,
		click_filter_event_type: clickFilterEventType,
		click_filter_target_group: clickFilterTargetGroup
	};

	if (shoudLogTracking()) {
		console.log('FILTERS: ', filterTrackingParams);
	}

	window?.utag?.link(filterTrackingParams);
}

export function trackAction(area: ActionArea, style: ActionStyle, topic: string, number = 0): void {
	const relValue = `${area}.${style}.${formatTealiumRelContent(topic)}${
		number > 0 ? `.${number}` : ''
	}`;

	if (shoudLogTracking()) {
		console.log('LINK: ', relValue);
	}

	window?.utag?.link({ wt_link_id: relValue });
}

export function trackPageView(params: IEventPageTypeParams): void {
	if (shoudLogTracking()) {
		console.log('VIEW (utag.view): ', params);
	}
	window?.utag?.view(params);
}

export function setPageView(params: IEventPageTypeParams): void {
	if (shoudLogTracking()) {
		console.log('VIEW (utag.data): ', params);
	}
	window.utag_data = params;
}

export function getDataTealiumRel(
	area: ActionArea,
	style: ActionStyle,
	topic: string,
	number = 0
): string {
	return `${area}.${style}.${formatTealiumRelContent(topic)}${number > 0 ? `.${number}` : ''}`;
}

export type ActionArea =
	| 'header'
	| 'navi-top'
	| 'footer'
	| 'content'
	| 'layer-search'
	| 'layer-content';

export type ActionStyle =
	| 'image'
	| 'text'
	| 'button'
	| 'video'
	| 'tab'
	| 'button-image'
	| 'button-text'
	| 'image-text'
	| 'button-image-text'
	// not a typo - defined like this in concept
	| 'accordeon'
	| 'radio'
	| 'checkbox';

export type EventPageType =
	// index page
	| 'theme.landing'
	// detail page and slot overview in checkout
	| 'event.individual'
	// checkout step with customer form data
	| 'event.customerdata'
	// sumary about event slot and customer data
	| 'event.summary'
	// appointment booking requested -> sending email link
	| 'event.confirmation'
	// email confirmation link -> accept or reject slot
	| 'event.mailconfirmation'
	// event slot was confirmed and is now booked
	| 'event.bookingconfirmation'
	// profige page with appointment overview
	| 'event.customeroverview'
	// when event is already full
	| 'event.occupied';

export type UserType = 'bestandskunde' | 'nicht-bestandskunde';

export type LoginStatus = 'logged-in' | 'not-logged-in';

export type IEventPageTypeParams =
	| {
			page_type: Omit<EventPageType, 'theme.landing' | 'error.common'>;
			page_category_1: 'event';
			page_category_2: 'event';
			page_category_3: 'event';
			page_content_id: string;
			page_product_catalog: 'event';
			shop_customer_type: UserType;
			user_type: UserType;
			user_login_status: LoginStatus;
			event_name: string;
			event_location: string;
			event_date: string; // date in dd.mm.yyyy format
	  }
	| {
			page_type: 'theme.landing' | 'error.common';
			page_category_1: 'event';
			page_category_2: 'event';
			page_category_3: 'event';
			page_content_id: string;
			page_product_catalog: 'event';
			shop_customer_type: UserType;
			user_type: UserType;
			user_login_status: LoginStatus;
	  };

export const getUserType = (): UserType =>
	Cookies.get('shop_customer_type_persist') === 'bestandskunde'
		? 'bestandskunde'
		: 'nicht-bestandskunde';

export const getTrackingEnvironment = (): 'dev' | 'qa' | 'prod' => {
	const utagEnvPathPart: 'dev' | 'qa' | 'prod' =
		process.env.NODE_ENV === 'development' ? 'dev' : 'prod';
	return utagEnvPathPart;
};
